import { useEffect, useState } from 'react';

/**
 * Auto-detect whether a rigged footballer GLB has been dropped into
 * `apps/web2/public/footballer-rigged.glb`. PlayerToken3D uses the
 * result to swap between RiggedFootballer (skinned, animated) and
 * ProceduralFootballer (capsules, idle bob). Probe runs once per
 * page load via a single HEAD fetch on the canonical (midfielder)
 * URL — variant URLs are assumed to be present alongside.
 */
const RIGGED_GLB_URL = '/footballer-rigged.glb';

/**
 * Body archetypes generated by apps/web2/scripts/generate-footballer.py.
 * Each maps to a sibling GLB in /public so the squad reads as 4 distinct
 * silhouettes (lean striker, stocky centreback, tall keeper, baseline
 * midfielder) rather than 22 clones.
 */
export type FootballerArchetype = 'midfielder' | 'striker' | 'centreback' | 'keeper';

export const ARCHETYPE_URLS: Record<FootballerArchetype, string> = {
  midfielder: '/footballer-rigged.glb',
  striker:    '/footballer-rigged-striker.glb',
  centreback: '/footballer-rigged-centreback.glb',
  keeper:     '/footballer-rigged-keeper.glb',
};

/** Map a formation `PlayerSpot.role` (GK / CB / LB / CM / ST / etc.) to
 *  the body archetype it should render with. Forwards play lean and fast,
 *  defenders broad and stocky, keepers tall, midfielders baseline. */
export function archetypeForRole(role: string | null | undefined): FootballerArchetype {
  if (!role) return 'midfielder';
  const r = role.toUpperCase();
  if (r === 'GK') return 'keeper';
  if (['CB', 'LB', 'RB', 'LWB', 'RWB', 'SW'].includes(r)) return 'centreback';
  if (['LW', 'RW', 'ST', 'CF', 'SS'].includes(r)) return 'striker';
  // Default everything midfield-y (CM, CDM, CAM, DM, AM, LM, RM, …) here.
  return 'midfielder';
}

let probePromise: Promise<boolean> | null = null;

export function probeRiggedFootballer(): Promise<boolean> {
  if (!probePromise) {
    // Vite's dev server returns index.html (with status 200!) for any
    // unmatched URL, so we can't trust `ok` alone — must verify the
    // server actually returned a GLB (model/gltf-binary content-type).
    probePromise = fetch(RIGGED_GLB_URL, { method: 'HEAD' })
      .then((r) => {
        if (!r.ok) return false;
        const ct = (r.headers.get('content-type') ?? '').toLowerCase();
        return ct.includes('gltf') || ct.includes('octet-stream') || ct.includes('model/');
      })
      // Network error / placeholder host / file missing — fall back.
      .catch(() => false);
  }
  return probePromise;
}

export function useRiggedFootballer(): boolean {
  const [available, setAvailable] = useState<boolean>(false);
  useEffect(() => {
    let cancelled = false;
    probeRiggedFootballer().then((ok) => {
      if (!cancelled) setAvailable(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return available;
}

export const RIGGED_FOOTBALLER_URL = RIGGED_GLB_URL;
